import type { FeatureAnnouncement, FeatureInteractions } from '../models';
import { isFeatureAnnouncement } from '../validation';

export interface ParsedFeatureAnnouncementManifest {
  features: FeatureAnnouncement[];
  authoritative: boolean;
}

export function parseFeatureAnnouncementDate(value: string): number {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);

    return Date.UTC(year, month - 1, day);
  }

  return Date.parse(value);
}

export function getFeatureAnnouncementRetentionLowerBound(
  now: number,
  retentionMonths: number,
): number {
  const currentMonth = new Date(now);

  return Date.UTC(
    currentMonth.getUTCFullYear(),
    currentMonth.getUTCMonth() - retentionMonths + 1,
    1,
  );
}

export function isFeatureAnnouncementWithinRetentionWindow(
  feature: FeatureAnnouncement,
  now: number,
  lowerBound: number,
): boolean {
  const publishedAt = parseFeatureAnnouncementDate(feature.publishedAt);

  return publishedAt >= lowerBound && publishedAt <= now;
}

export function getActiveFeatureAnnouncements(
  features: FeatureAnnouncement[],
  now: number,
  retentionMonths: number,
): FeatureAnnouncement[] {
  const lowerBound = getFeatureAnnouncementRetentionLowerBound(
    now,
    retentionMonths,
  );

  return features
    .filter(
      (feature) =>
        isFeatureAnnouncementWithinRetentionWindow(feature, now, lowerBound) &&
        (!feature.expiryDate ||
          parseFeatureAnnouncementDate(feature.expiryDate) > now),
    )
    .sort(
      (first, second) =>
        Date.parse(second.publishedAt) - Date.parse(first.publishedAt),
    );
}

export function parseFeatureAnnouncementManifest(
  manifest: unknown,
  ownerDocument: Document,
): ParsedFeatureAnnouncementManifest {
  if (!Array.isArray(manifest)) {
    return { features: [], authoritative: false };
  }

  const keys = new Set<string>();
  const features: FeatureAnnouncement[] = [];
  let authoritative = true;

  manifest.forEach((candidate: unknown) => {
    if (
      !isFeatureAnnouncement(candidate, ownerDocument) ||
      keys.has(candidate.featureKey)
    ) {
      authoritative = false;

      return;
    }

    keys.add(candidate.featureKey);
    features.push(candidate);
  });

  return { features, authoritative };
}

export function pruneFeatureInteractions(
  interactions: FeatureInteractions,
  features: FeatureAnnouncement[],
  now: number,
  retentionMonths: number,
): FeatureInteractions {
  const lowerBound = getFeatureAnnouncementRetentionLowerBound(
    now,
    retentionMonths,
  );
  const eligibleKeys = new Set(
    features
      .filter((feature) =>
        isFeatureAnnouncementWithinRetentionWindow(feature, now, lowerBound),
      )
      .map((feature) => feature.featureKey),
  );

  return Object.fromEntries(
    Object.entries(interactions).filter(([featureKey]) =>
      eligibleKeys.has(featureKey),
    ),
  );
}

export function featureInteractionsEqual(
  first: FeatureInteractions,
  second: FeatureInteractions,
): boolean {
  const firstEntries = Object.entries(first);
  const secondEntries = Object.entries(second);

  return (
    firstEntries.length === secondEntries.length &&
    firstEntries.every(([key, value]) => {
      const other = second[key];

      return (
        Object.prototype.hasOwnProperty.call(second, key) &&
        value.seenAt === other.seenAt &&
        value.tourCompleted === other.tourCompleted
      );
    })
  );
}
